/**
 * RichTextEditor — a token-styled WYSIWYG editor for the Closing Email
 * Template Editor. Migrated from nuadvisory's TinyMCE `templatePage.js`.
 *
 * Greenfield dependency: TipTap (headless ProseMirror). It lives in
 * `packages/web` (NOT `@nu-assist/ui`) on purpose — `ui`'s external-dep
 * allow-list is Radix / Tailwind / lucide / sonner only, and TipTap is not
 * on it. One consumer today (the 2x rule says don't extract a primitive yet).
 *
 * Output is HTML (`editor.getHTML()`), compatible with the nuadvisory
 * email-send path that consumes the stored template `content`.
 *
 * Content model: the editor owns its content internally. The parent reads
 * edits via `onChange` and pushes external content (template selection /
 * reset) through the imperative `setContent` handle — so loading a template
 * never fights a controlled-value sync loop or jumps the cursor.
 */
import { forwardRef, useEffect, useImperativeHandle, useRef, type ReactNode } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, List, ListOrdered } from "lucide-react";

export interface Placeholder {
  label: string;
  token: string;
}

export interface RichTextEditorHandle {
  /** Replace the editor's content without emitting an onChange. */
  setContent: (html: string) => void;
}

interface RichTextEditorProps {
  /** Initial HTML the editor mounts with. */
  initialContent: string;
  /** Fired on every user edit with the editor's current HTML. */
  onChange: (html: string) => void;
  /** Tokens the Placeholders dropdown can insert at the cursor. */
  placeholders: Placeholder[];
}

interface ToolbarButtonProps {
  active: boolean;
  label: string;
  onClick: () => void;
  children: ReactNode;
}

function ToolbarButton({ active, label, onClick, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      // Keep the editor selection while clicking the toolbar.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`rounded-sm p-1.5 transition-colors ${
        active ? "bg-accent-muted text-accent" : "text-text-secondary hover:bg-bg hover:text-text"
      }`}
    >
      {children}
    </button>
  );
}

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor({ initialContent, onChange, placeholders }, ref) {
    const editor = useEditor({
      extensions: [StarterKit],
      content: initialContent,
      // Avoid the React 19 StrictMode double-render warning (CSR only).
      immediatelyRender: false,
      onUpdate: ({ editor }) => onChange(editor.getHTML()),
    });

    // If the parent sets content (template selection / reset) before the
    // editor has finished mounting (`immediatelyRender: false` defers
    // creation to a post-mount effect), `editor` is still null. Stash the
    // last requested HTML and apply it once the editor is ready, so a fast
    // selection can't leave the editor showing the seed content.
    const pendingContentRef = useRef<string | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        setContent: (html: string) => {
          if (editor) editor.commands.setContent(html, { emitUpdate: false });
          else pendingContentRef.current = html;
        },
      }),
      [editor]
    );

    useEffect(() => {
      if (editor && pendingContentRef.current !== null) {
        editor.commands.setContent(pendingContentRef.current, { emitUpdate: false });
        pendingContentRef.current = null;
      }
    }, [editor]);

    const insertPlaceholder = (token: string) => {
      if (!token) return;
      editor?.chain().focus().insertContent(token).run();
    };

    return (
      <div className="rounded-md border border-border bg-surface focus-within:border-border-hover">
        <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
          <ToolbarButton
            active={Boolean(editor?.isActive("bold"))}
            label="Bold"
            onClick={() => editor?.chain().focus().toggleBold().run()}
          >
            <Bold size={16} aria-hidden />
          </ToolbarButton>
          <ToolbarButton
            active={Boolean(editor?.isActive("italic"))}
            label="Italic"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          >
            <Italic size={16} aria-hidden />
          </ToolbarButton>
          <ToolbarButton
            active={Boolean(editor?.isActive("bulletList"))}
            label="Bullet list"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
          >
            <List size={16} aria-hidden />
          </ToolbarButton>
          <ToolbarButton
            active={Boolean(editor?.isActive("orderedList"))}
            label="Numbered list"
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered size={16} aria-hidden />
          </ToolbarButton>

          <span className="mx-1 h-5 w-px bg-border" aria-hidden />

          <select
            aria-label="Insert placeholder"
            value=""
            onChange={(e) => insertPlaceholder(e.target.value)}
            className="rounded-sm border border-border bg-surface px-2 py-1 text-caption text-text-secondary focus:outline-none focus:ring-2 focus:ring-accent"
          >
            <option value="">Placeholders…</option>
            {placeholders.map((p) => (
              <option key={p.token} value={p.token}>
                {p.label}
              </option>
            ))}
          </select>
        </div>

        <EditorContent
          editor={editor}
          className="text-body text-text [&_.ProseMirror]:min-h-64 [&_.ProseMirror]:px-3 [&_.ProseMirror]:py-2 [&_.ProseMirror]:outline-none [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-6 [&_.ProseMirror_p]:mb-2 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-6"
        />
      </div>
    );
  }
);
